import React, { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/core';
import type { SelectionBookmark, Transaction } from '@tiptap/pm/state';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { ClipboardContent, PasteMode, pasteClipboardContent, readClipboardContent } from './paste-options';
import { insideFieldContainer } from './field-container';

export function PasteMenu({ editor, onError, disabled = false }: { editor: Editor; onError: (message: string) => void; disabled?: boolean }) {
    const menu = useRef<Menu>(null);
    const bookmark = useRef<SelectionBookmark | null>(null);
    const generation = useRef(0);
    const errorHandler = useRef(onError);
    errorHandler.current = onError;
    const [content, setContent] = useState<ClipboardContent | null>(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        generation.current++;
        bookmark.current = null;
        setContent(null); setLoading(false);
        const mapSelection = ({ transaction }: { transaction: Transaction }) => {
            if (bookmark.current) bookmark.current = bookmark.current.map(transaction.mapping);
        };
        editor.on('transaction', mapSelection);
        const onKeyDown = (event: KeyboardEvent) => {
            if (insideFieldContainer(editor)) return;
            if (!editor.isEditable || event.isComposing || event.getModifierState('AltGraph') || !(event.ctrlKey || event.metaKey)
                || !(event.code === 'KeyV' || event.key.toLowerCase() === 'v')) return;
            if (!event.altKey || event.shiftKey) {
                // A subsequent ordinary paste supersedes a pending asynchronous read.
                generation.current++;
                bookmark.current = null;
                setLoading(false);
                return;
            }
            event.preventDefault();
            event.stopImmediatePropagation();
            if (event.repeat) return;
            const current = ++generation.current;
            setLoading(false);
            bookmark.current = editor.state.selection.getBookmark();
            // Start the clipboard read during the key gesture, before yielding activation.
            void readClipboardContent().then(clipboard => {
                if (current !== generation.current || editor.isDestroyed || !editor.isEditable) return;
                if (bookmark.current) editor.view.dispatch(editor.state.tr.setSelection(bookmark.current.resolve(editor.state.doc)));
                bookmark.current = null;
                editor.view.focus();
                pasteClipboardContent(editor, clipboard, clipboard.hasText ? 'no-colors' : 'original');
            }).catch(() => {
                if (current === generation.current) errorHandler.current('The browser could not read the clipboard. Allow clipboard access and try again, or use Ctrl+V in the editor.');
            });
        };
        const dom = editor.view.dom;
        const dismissMenu = (event: Event) => menu.current?.hide(event as unknown as React.SyntheticEvent);
        dom.addEventListener('pointerdown', dismissMenu, true);
        dom.addEventListener('click', dismissMenu, true);
        dom.addEventListener('keydown', onKeyDown, true);
        return () => {
            generation.current++;
            editor.off('transaction', mapSelection);
            dom.removeEventListener('pointerdown', dismissMenu, true);
            dom.removeEventListener('click', dismissMenu, true);
            dom.removeEventListener('keydown', onKeyDown, true);
        };
    }, [editor]);

    const open = async (event: React.MouseEvent<HTMLButtonElement>) => {
        if (insideFieldContainer(editor)) return;
        const current = ++generation.current;
        bookmark.current = editor.state.selection.getBookmark();
        setContent(null); setLoading(true);
        menu.current?.toggle(event);
        try {
            const clipboard = await readClipboardContent();
            if (current === generation.current && !editor.isDestroyed) setContent(clipboard);
        } catch {
            if (current === generation.current) onError('The browser could not read the clipboard. Allow clipboard access and try again, or use Ctrl+V in the editor.');
        } finally {
            if (current === generation.current) setLoading(false);
        }
    };
    const paste = (mode: PasteMode) => {
        if (insideFieldContainer(editor)) return;
        if (!content || editor.isDestroyed) return;
        try {
            if (bookmark.current) editor.view.dispatch(editor.state.tr.setSelection(bookmark.current.resolve(editor.state.doc)));
            bookmark.current = null;
            editor.view.focus();
            pasteClipboardContent(editor, content, mode);
        } catch { onError('Unable to paste the clipboard content. Try Ctrl+V in the editor.'); }
    };
    return <>
        <Button className="ce-btn" icon="pi pi-clipboard" text aria-label="Paste options" title="Paste options"
            data-pr-tooltip="Paste options" disabled={disabled || !editor.isEditable} onClick={open} />
        <Menu popup ref={menu} model={[
            { label: loading ? 'Reading clipboard…' : 'Paste', disabled: !content, command: () => paste('original') },
            { label: 'Paste text only', disabled: !content?.hasText, command: () => paste('text') },
            { label: 'Paste without foreground/background (Ctrl+Alt+V)', disabled: !content?.hasText, command: () => paste('no-colors') },
            { label: 'Paste without background', disabled: !content?.hasText, command: () => paste('no-background') },
            { label: 'Paste without foreground', disabled: !content?.hasText, command: () => paste('no-foreground') },
        ]} />
    </>;
}
